import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import CommonFooter from "../../core/common/footer/commonFooter";
import { MinusCircle, PlusCircle } from "feather-icons-react/build/IconComponents";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import Barcode from "react-barcode";
import { Circles } from "react-loader-spinner";
import { api } from "../../api/client";

// ---------- S3 helpers (same convention as subcategory.jsx) ----------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/"); // fix legacy typo
  if (!/^catalog\//i.test(v)) {
    v = `catalog/${v.split("/").pop()}`;
  }
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already full URL
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};

// Be defensive: handle string / array / object shapes for images
const resolveProductImage = (images) => {
  if (!images) return "";

  // If array, prefer first usable entry
  if (Array.isArray(images)) {
    const first = images[0];
    if (typeof first === "string") return resolveProductImage(first);
    if (first && typeof first === "object") {
      return resolveProductImage(first.url || first.key || first.src || first.image);
    }
  }

  // If object
  if (images && typeof images === "object") {
    const v = images.url || images.key || images.src || images.image;
    return resolveProductImage(v);
  }

  // String
  const s = String(images);
  if (s.startsWith("data:") || /^https?:\/\//i.test(s)) return s; // data URL or full http(s)
  return toPublicUrl(s); // treat as catalog key (or filename)
};
// ---------------------------------------------------------------------

const PrintBarcode = () => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [productList, setProductList] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [storeOptions, setStoreOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const visibleProducts = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return productList;

    const includes = (s) => (s || "").toString().toLowerCase().includes(q);

    return productList.filter(
      (p) =>
        includes(p?.productName?.en) ||
        includes(p?.sku) ||
        includes(p?.itemCode)
    );
  }, [productList, searchQuery]);

  const handleDecrement = (productId) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max((prev[productId] || 1) - 1, 1),
    }));
  };

  const handleIncrement = (productId) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: (prev[productId] || 1) + 1,
    }));
  };

  const handleQuantityChange = (productId, value) => {
    const numeric = parseInt(value);
    if (!isNaN(numeric) && numeric > 0) {
      setQuantities((prev) => ({ ...prev, [productId]: numeric }));
    }
  };

  useEffect(() => {
    const fetchStoresAndWarehouses = async () => {
      try {
        const [storeRes, warehouseRes] = await Promise.all([
          api.get(`${process.env.REACT_APP_API_BASE_URL}api/store/stores`),
          api.get(`${process.env.REACT_APP_API_BASE_URL}api/warehouse/list`),
        ]);

        const stores = (storeRes.data || []).map((store) => ({
          value: store._id,
          label: store.storeName?.en || store.CustomerName || "Unnamed Store",
        }));

        const warehouses = (warehouseRes.data || []).map((warehouse) => ({
          value: warehouse._id,
          label:
            warehouse.warehouseName?.en ||
            warehouse.warehouse ||
            "Unnamed Warehouse",
        }));

        setStoreOptions(stores);
        setWarehouseOptions(warehouses);
      } catch (err) {
        console.error("Failed to fetch stores or warehouses", err);
      }
    };

    fetchStoresAndWarehouses();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `${process.env.REACT_APP_API_BASE_URL}api/products/barcode-list`,
          {
            params: {
              storeId: selectedStore?.value,
              warehouseId: selectedWarehouse?.value,
              search: searchQuery,
            },
          }
        );
        setProductList(response.data || []);
      } catch (error) {
        console.error("Error fetching products for barcode:", error);
      } finally {
        setLoading(false);
      }
    };

    const isValidMongoId = (id) => /^[a-f\d]{24}$/i.test(id);

    if (
      (selectedStore?.value && !isValidMongoId(selectedStore.value)) ||
      (selectedWarehouse?.value && !isValidMongoId(selectedWarehouse.value))
    ) {
      console.warn("Invalid storeId or warehouseId – skipping fetch.");
      setProductList([]);
      return;
    }

    if (selectedStore?.value || selectedWarehouse?.value || searchQuery) {
      fetchProducts();
    } else {
      setProductList([]);
    }
  }, [selectedStore, selectedWarehouse, searchQuery]);

  const handleRemoveFromTable = (productId) => {
    setProductList((prev) => prev.filter((product) => product._id !== productId));
  };

  return (
    <>
      <div className="page-wrapper notes-page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Print Barcode")}</h4>
                <h6>{window.translate("Manage your barcodes")}</h6>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <ul className="table-top-head">
                <RefreshIcon />
                <CollapesIcon />
              </ul>
            </div>
          </div>

          <div className="barcode-content-list">
            <form>
              <div className="row">
                <div className="col-lg-6 col-12">
                  <div className="row seacrh-barcode-item mb-1">
                    <div className="col-sm-6 mb-3 seacrh-barcode-item-one">
                      <label className="form-label">
                        {window.translate("Warehouse")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={warehouseOptions}
                        value={selectedWarehouse}
                        onChange={(option) => setSelectedWarehouse(option)}
                        placeholder="Choose"
                      />
                    </div>
                    <div className="col-sm-6 mb-3 seacrh-barcode-item-one">
                      <label className="form-label">
                        {window.translate("Store")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={storeOptions}
                        value={selectedStore}
                        onChange={(option) => setSelectedStore(option)}
                        placeholder="Choose"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <div className="mb-3 search-form seacrh-barcode-item">
                    <div className="searchInput">
                      <label className="form-label">
                        {window.translate("Product")}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control text-gray-3"
                        placeholder="Search Product by Code"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <div className="resultBox"></div>
                      <div className="icon">
                        <i className="fas fa-search" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <div className="col-lg-12">
              <div className="modal-body-table search-modal-header bg-light p-2 p-sm-4">
                <div className="table-responsive border rounded-1 barcode-table">
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "200px" }}
                    >
                      <Circles height={60} width={60} color="#0d6efd" ariaLabel="loading" />
                    </div>
                  ) : (
                    <table className="table datatable mb-0">
                      <thead>
                        <tr>
                          <th>{window.translate("Product")}</th>
                          <th>{window.translate("SKU")}</th>
                          <th>{window.translate("Code")}</th>
                          <th>{window.translate("Qty")}</th>
                          <th className="text-center no-sort bg-secondary-transparent" />
                        </tr>
                      </thead>
                      <tbody>
                        {visibleProducts.map((product, index) => {
                          const imgSrc =
                            resolveProductImage(product?.images) ||
                            "/assets/img/no-image.png";

                          return (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <Link to="#" className="avatar avatar-md me-2">
                                    <img
                                      src={imgSrc}
                                      alt={product?.productName?.en || "Product"}
                                      className="img-fluid"
                                      style={{
                                        width: 40,
                                        height: 40,
                                        objectFit: "cover",
                                        borderRadius: 6,
                                      }}
                                    />
                                  </Link>
                                  <Link to="#">{product?.productName?.en}</Link>
                                </div>
                              </td>
                              <td>{product?.sku}</td>
                              <td>{product?.itemCode}</td>
                              <td>
                                <div className="product-quantity border-secondary-transparent">
                                  <span
                                    className="quantity-btn"
                                    onClick={() => handleDecrement(product._id)}
                                  >
                                    <MinusCircle className="feather-search" />
                                  </span>
                                  <input
                                    type="text"
                                    className="quntity-input"
                                    value={quantities[product._id] || 1}
                                    onChange={(e) =>
                                      handleQuantityChange(product._id, e.target.value)
                                    }
                                  />
                                  <span
                                    className="quantity-btn"
                                    onClick={() => handleIncrement(product._id)}
                                  >
                                    <PlusCircle className="plus-circle" />
                                  </span>
                                </div>
                              </td>
                              <td className="action-table-data">
                                <div className="edit-delete-action">
                                  <Link
                                    className="barcode-delete-icon"
                                    to="#"
                                    onClick={() => handleRemoveFromTable(product._id)}
                                  >
                                    <i
                                      data-feather="x-circle"
                                      className="feather-x-circle text-danger"
                                    />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="paper-search-size">
              <div className="row align-items-center">
                <div className="col-lg-6">
                  <form className="mb-0">{/* Paper size controls (optional) */}</form>
                </div>
                <div className="col-lg-6 pt-3">{/* toggles (optional) */}</div>
              </div>
            </div>

            <div className="search-barcode-button">
              <Link
                to="#"
                className="btn btn-submit btn-primary me-2 mt-0"
                data-bs-toggle="modal"
                data-bs-target="#prints-barcode"
              >
                <span>
                  <i className="fas fa-eye ms-2 me-2" />
                </span>
                {window.translate("Generate Barcode")}
              </Link>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Print Barcode */}
      <div className="modal fade" id="prints-barcode">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal barcode-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header">
                  <div className="page-title">
                    <h4>{window.translate("barcode")}</h4>
                  </div>
                  <button
                    type="button"
                    className="close bg-danger text-white fs-16 shadow-none"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>

                <div className="modal-body">
                  <div className="d-flex justify-content-end">
                    <Link
                      to="#"
                      className="btn btn-cancel close-btn btn-danger shadow-none"
                      style={{ display: "flex", gap: 8 }}
                      onClick={() => window.print()}
                    >
                      <span>
                        <i className="fas fa-print me-2" />
                      </span>
                      {window.translate("printBarcode")}
                    </Link>
                  </div>

                  {visibleProducts.map((product) => {
                    const qty = quantities[product._id] || 1;
                    const productName = product?.productName?.en || "Product";
                    const storeName = product?.store?.storeName?.en || "Store";
                    const price = product?.price || 0;
                    const barcodeValue = product?.itemCode || product?.sku || "";

                    return (
                      <React.Fragment key={product._id}>
                        <div className="barcode-scan-header">
                          <h5>{productName}</h5>
                        </div>
                        <div className="row">
                          {Array.from({ length: qty }).map((_, i) => (
                            <div className="col-sm-4" key={i}>
                              <div
                                className="barcode-scanner-link text-center border rounded"
                                style={{
                                  minHeight: "200px",
                                  padding: "16px 12px",
                                  backgroundColor: "#fff",
                                  boxShadow: "0 0 6px rgba(0,0,0,0.08)",
                                  marginBottom: "20px",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div>
                                  <h6 style={{ fontWeight: 600, marginBottom: 6 }}>
                                    {storeName}
                                  </h6>
                                  <p style={{ margin: "0 0 4px" }}>{productName}</p>
                                  <p style={{ margin: "0 0 12px" }}>
                                    {window.translate("Price")}: {price}
                                  </p>
                                </div>
                                <div
                                  className="barscaner-img"
                                  style={{
                                    padding: 6,
                                    backgroundColor: "#f9f9f9",
                                    borderRadius: 4,
                                    overflowX: "auto",
                                  }}
                                >
                                  <Barcode
                                    value={barcodeValue}
                                    height={40}
                                    width={1.5}
                                    fontSize={12}
                                    displayValue={true}
                                    margin={0}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Print Barcode */}

      <CommonDeleteModal />
    </>
  );
};

export default PrintBarcode;
